import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import styled from 'styled-components'
import { ContentWidth, TextWidth } from '@ta-interaktiv/react-disco-grid'
import { GlobalVariables } from 'Globals/GlobalVariables'
import { MapBoxMap } from 'components/MapBoxMap'
import { StationSelect } from 'components/StationSelect'
import { useEffect, useState } from 'react'
import mapboxgl, { Expression, LngLatBoundsLike, LngLatLike } from 'mapbox-gl'
import { TimeSelect } from 'components/TimeSelect'
import { scaleLinear } from 'd3-scale'
import { useWindowSize } from 'usehooks-ts'
import { ThreeDots } from 'react-loader-spinner'

const maxbounds: LngLatBoundsLike = [
  [-16.8026819225, 32.4568277902],
  [33.8411532463, 61.2330974837],
]

const center: LngLatLike = [8.252106, 46.900026]
export const colorscale = scaleLinear<string, string>().domain([3, 10])

const startStations = {
  tagesanzeiger: '8503000', // Züri HB
  bernerzeitung: '8507000', // Bern
  bazonline: '8500010', // Basel SBB
  berneroberlaender: '8507000', // Bern
  langenthalertagblatt: '8507000', // Bern
  thunertagblatt: '8507000', // Bern
  derbund: '8507000', // Bern
  landbote: '8506000', // Winterthur
  zuonline: '8503000', // Züri HB
  zsz: '8503000', // Züri HB
  "24heures": '8501008', // Genf
  tdg: '8501008', // Genf
}

//'-2.524281,37.985746,22.041149,56.162663'

const darkModeStyle_de =
  'mapbox://styles/ta-interaktiv/clk26b7o000d201qr8xhr4uml'
const lightModeStyle_de =
  'mapbox://styles/ta-interaktiv/clia6dh9j004z01qr0opvce9m'
const darkModeStyle_fr =
  'mapbox://styles/ta-interaktiv/clk26b7o000d201qr8xhr4uml/draft'
const lightModeStyle_fr =
  'mapbox://styles/ta-interaktiv/clm5wdzgi00w301pjbfbv63re/draft'
const fillSortLocekd = [
  'step',
  ['get', 'time'],
  10,
  2,
  9,
  3,
  8,
  4,
  7,
  5,
  6,
  6,
  5,
  7,
  4,
  8,
  3,
  9,
  2,
  10,
  1,
] as Expression

const locales_de = {
  stationSelect: {
    label: 'Startbahnhof',
    placeholder: 'Bahnhof auswählen',
  },
  timeSelect: {
    label: 'Maximale Reisezeit auswählen',
  },
  map: {
    back: 'Zurück zum Artikel',
    fullscreen: 'Vollbild',
  },
}

const locales_fr = {
  stationSelect: {
    label: 'Gare de départ',
    placeholder: 'Sélectionnez une gare',
  },
  timeSelect: {
    label: 'Sélectionnez le temps de trajet maximal',
  },
  map: {
    back: 'Retour à l’article',
    fullscreen: 'Plein écran',
  },
}

const MainComponent = ({
  tenant,
  colorMode,
  properties,
}: MicroFrontendProps) => {
  const { width } = useWindowSize()
  const isMobile = width < 712
  const zoom = isMobile ? 3 : 4.25
  const maxzoom = isMobile ? 11 : 11
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false)
  const [isLodaing, setIsLoading] = useState<boolean>(false)
  const [map, setMap] = useState<mapboxgl.Map>()
  const [time, setTime] = useState<number>(10)
  const [stationId, setStationId] = useState<string>()

  const locales = tenant.lang === 'de' ? locales_de : locales_fr
  console.log(tenant)
  colorscale.range(
    colorMode === 'dark' ? ['#378ebd', '#FFE462'] : ['#FFE462', '#378ebd']
  )

  function addShapeSource(map: mapboxgl.Map) {
    if (!map.getSource('locked')) {
      map.addSource('locked', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [],
          },
        },
      })
    }
  }
  function addShapeLayer(map: mapboxgl.Map) {
    if (!map.getLayer('locked')) {
      map.addLayer(
        {
          id: 'locked',
          type: 'fill',
          source: 'locked',
          layout: {
            'fill-sort-key': fillSortLocekd,
          },
          paint: {
            'fill-color': [
              'interpolate',
              ['linear'],
              ['get', 'time'],
              2,
              'transparent',
              2.5,
              colorscale(3),
              time,
              colorscale(time),
              time + 1,
              'transparent',
            ],
            'fill-opacity': 1,
            'fill-outline-color': [
              'interpolate',
              ['linear'],
              ['get', 'time'],
              time - 1,
              'rgba(0, 0, 0, 0)',
              time,
              'rgba(0, 0, 0, .5)',
              time + 1,
              'rgba(0, 0, 0, 0)',
            ],
          },
        },
        'stations'
      )
    }
  }
  function loadStationData(map: mapboxgl.Map) {
    setIsLoading(true)
    fetch(
      'https://interaktiv.tagesanzeiger.ch/2023/traveltimes-data/Simplified/' +
        stationId +
        '.json'
    )
      .then((res) => res.json())
      .then((data) => {
        const source = map.getSource('locked') as any
        map.once('idle', (e) => source.setData(data))
        source && source.setData(data)
        const filter = [
          'all',
          ['match', ['get', 'unqiue_id'], [+stationId], true, false],
        ]
        map.setFilter('stationlabels', filter)
        setIsLoading(false)
      })
  }
  function handleChangeViaSelect(id: string) {
    setStationId(id)
  }
  function adjustColorAndOutline(time: number, map: mapboxgl.Map) {
    map.setPaintProperty('locked', 'fill-color', [
      'interpolate',
      ['linear'],
      ['get', 'time'],
      2,
      'transparent',
      2.5,
      colorscale(3),
      time,
      colorscale(time),
      time + 1,
      'transparent',
    ])
    map.setPaintProperty('locked', 'fill-outline-color', [
      'interpolate',
      ['linear'],
      ['get', 'time'],
      time - 1,
      'rgba(0, 0, 0, 0)',
      time,
      '#000000',
      time + 1,
      'rgba(0, 0, 0, 0)',
    ])
  }

  useEffect(() => {
    setStationId(
      startStations[tenant.key] ? startStations[tenant.key] : '8503000'
    )
  }, [])
  useEffect(() => {
    setStationId(
      startStations[tenant.key] ? startStations[tenant.key] : '8503000'
    )
  }, [map])

  useEffect(() => {
    if (!map) return
    // map.fitBounds(box)
    map.setMaxBounds(maxbounds)
    map.setCenter(center)
    map.setMinZoom(zoom)
    map.setMaxZoom(maxzoom)
  }, [width])

  useEffect(() => {
    if (!map || !stationId) return
    // map.fitBounds(box)
    map.setCenter(center)
    map.setMaxBounds(maxbounds)

    map.setMinZoom(zoom)
    map.setMaxZoom(maxzoom)

    loadStationData(map)
  }, [stationId])

  useEffect(() => {
    if (!map) return
    adjustColorAndOutline(time, map)
  }, [time])

  useEffect(() => {
    if (!map) return

    const mapstyle =
      colorMode === 'dark'
        ? tenant.lang === 'de'
          ? darkModeStyle_de
          : darkModeStyle_fr
        : tenant.lang === 'de'
        ? lightModeStyle_de
        : lightModeStyle_fr
    console.log(mapstyle)
    map.setStyle(mapstyle)

    map.once('styledata', () => {
      addShapeSource(map)
      addShapeLayer(map)
      loadStationData(map)
      adjustColorAndOutline(time, map)
    })
  }, [colorMode])

  return (
    <AppContainer $colorMode={colorMode} isFullscreen={isFullscreen}>
      {!isFullscreen && (
        <TextWidth>
          <>
            <StationSelect
              onChange={(e) => e && handleChangeViaSelect(e.id)}
              current={stationId}
              isFullscreen={isFullscreen}
              locales={locales.stationSelect}
            />
            {!isMobile && (
              <TimeSelect
                onChange={setTime}
                current={time}
                isFullscreen={isFullscreen}
                locales={locales.timeSelect}
              />
            )}
          </>
        </TextWidth>
      )}
      <ContentWidth>
        <MapBoxMap
          init={{
            lat: center[1],
            lng: center[0],
            style:
              colorMode === 'dark'
                ? tenant.lang === 'de'
                  ? darkModeStyle_de
                  : darkModeStyle_fr
                : tenant.lang === 'de'
                ? lightModeStyle_de
                : lightModeStyle_fr,
            minZoom: zoom,
            maxZoom: maxzoom,
            zoom: zoom,

            // showNavigationControl: false,
          }}
          locales={locales.map}
          disableRotation
          disableZoom
          disableDrag
          disablePanZoom
          getMapOnInit={(map) => {
            setMap(map)
            map.on('styledata', () => {
              addShapeSource(map)
              addShapeLayer(map)
            })
          }}
          onFullScreenChange={(isFullscreen) => setIsFullscreen(isFullscreen)}
        >
          {isLodaing && <Loading />}
          <TextWidth>
            {isFullscreen && (
              <>
                <StationSelect
                  onChange={(e) => e && handleChangeViaSelect(e.id)}
                  current={stationId}
                  isFullscreen={isFullscreen}
                  locales={locales.stationSelect}
                />
              </>
            )}
            {isFullscreen && (
              <>
                <TimeSelect
                  onChange={setTime}
                  current={time}
                  isFullscreen={isFullscreen}
                  locales={locales.timeSelect}
                />
              </>
            )}
          </TextWidth>
        </MapBoxMap>
        <TextWidth>
          {isMobile && !isFullscreen && (
            <>
              <TimeSelect
                onChange={setTime}
                current={time}
                isFullscreen={isFullscreen}
                locales={locales.timeSelect}
              />
            </>
          )}
        </TextWidth>
      </ContentWidth>
    </AppContainer>
  )
}

const AppContainer = styled(TamediaStyles)<{ isFullscreen: boolean }>`${GlobalVariables}
  font-family: var(--font-sans-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  z-index: 404;

  input {
    font-size: 18px;
    border: 0 !important;
    border-color: var(--text-color) !important;
    border: 1px solid var(--text-color) !important;
    padding: 15px;
    color: var(--text-color) !important;
  }
  button {
    cursor: pointer !important;
  }
`
export default MainComponent

const Loading = () => {
  return (
    <LoadingContainer>
      <ThreeDots width='60' color='var(--yellow-main)' />
      Reisezeiten werden geladen
    </LoadingContainer>
  )
}

const LoadingContainer = styled.div`
  position: absolute;
  z-index: 401;
  left: 50%;
  top: 50%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  max-width: 200px;
  background-color: #202346;
  text-align: center;
  opacity: 0.75;
  border-radius: 10px;
  color: #fff;
  line-height: 1.2em;
  padding-bottom: 25px;
  transform: translate(-50%, -50%);
`
